/**
 * InventoryPage — Sprint 5.2D (Estoque + Reserva + Logística).
 *
 * Aba "Estoque" no NOC. Acompanha o equipamento ao longo do ciclo
 * logístico: estoque → reservado → instalado, além de manutenção e
 * perdido.
 *
 * - Dashboard de cards (contagem por status + offline).
 * - Filtros por status / offline.
 * - Tabela: QR · Device · FW · Status · Cliente · Instalador · HB · Template.
 * - Ações: Novo item, Reservar instalação, Editar, Marcar manutenção,
 *   Marcar perdido.
 *
 * Polling de 15s. Não interfere com o mapa, o installer nem o IXC.
 */
import { useCallback, useEffect, useState } from "react";
import {
  createInventoryItem,
  installInventoryItem,
  listInventory,
  reserveInventoryItem,
  updateInventoryItem,
} from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";
import Can from "../components/Can.jsx";
import { CAP } from "../lib/rbac.js";
import QrScannerModal from "../components/QrScannerModal.jsx";
import {
  isValidPhysicalQr,
  normalizePhysicalQr,
  PHYSICAL_QR_ERROR,
} from "../lib/physicalQr.js";

const POLL_MS = 15000;

// Filtros da barra superior. 'offline' é um pseudo-status.
const FILTERS = [
  { key: "", label: "inventory.filterAll" },
  { key: "stock", label: "inventory.statusStock" },
  { key: "reserved", label: "inventory.statusReserved" },
  { key: "installed", label: "inventory.statusInstalled" },
  { key: "offline", label: "inventory.filterOffline" },
  { key: "maintenance", label: "inventory.statusMaintenance" },
  { key: "lost", label: "inventory.statusLost" },
];

const STATUS_STYLE = {
  stock: "text-noc-accent bg-noc-accent/10 border-noc-accent/30",
  reserved: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
  installed: "text-noc-ok bg-noc-ok/10 border-noc-ok/30",
  maintenance: "text-noc-orange bg-noc-orange/10 border-noc-orange/30",
  lost: "text-noc-err bg-noc-err/10 border-noc-err/30",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLE[status] || "text-noc-muted bg-noc-bg/40";
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}
    >
      {t("inventory.status_" + status) || status}
    </span>
  );
}

export default function InventoryPage() {
  const [data, setData] = useState({
    items: [], total: 0, counts: {}, offline_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("");
  // Modais
  const [modal, setModal] = useState(null); // 'new' | 'reserve' | 'edit'
  const [editItem, setEditItem] = useState(null);

  const buildFilters = useCallback(() => {
    if (filter === "offline") return { offline: true };
    if (filter) return { status: filter };
    return {};
  }, [filter]);

  const load = useCallback((signal) => {
    listInventory(buildFilters(), { signal })
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, [buildFilters]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const reload = () => {
    const ac = new AbortController();
    load(ac.signal);
  };

  // ── Ações rápidas (Marcar manutenção / perdido) ────────────────────
  async function quickStatus(item, status) {
    try {
      await updateInventoryItem(item.id, { status });
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  const counts = data.counts || {};

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-noc-text">
          {t("inventory.title")}
        </h1>
        <div className="flex gap-2 flex-wrap">
          <Can capability={CAP.INVENTORY_WRITE}>
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-border
                       hover:bg-noc-bg/40 text-noc-accent"
            onClick={() => setModal("new")}
          >
            + {t("inventory.btnNew")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-warn/30
                       hover:bg-noc-warn/10 text-noc-warn"
            onClick={() => setModal("reserve")}
          >
            {t("inventory.btnReserve")}
          </button>
          </Can>
        </div>
      </div>

      {/* Dashboard de cards */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <DashCard label={t("inventory.statusStock")}
                  value={counts.stock || 0} tone="sky" />
        <DashCard label={t("inventory.statusReserved")}
                  value={counts.reserved || 0} tone="amber" />
        <DashCard label={t("inventory.statusInstalled")}
                  value={counts.installed || 0} tone="emerald" />
        <DashCard label={t("inventory.filterOffline")}
                  value={data.offline_count || 0} tone="red" />
        <DashCard label={t("inventory.statusMaintenance")}
                  value={counts.maintenance || 0} tone="orange" />
        <DashCard label={t("inventory.statusLost")}
                  value={counts.lost || 0} tone="red" />
      </div>

      {/* Filtros */}
      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            className={
              "text-[11px] px-2 py-0.5 rounded border " +
              (filter === f.key
                ? "border-noc-accent/30 text-noc-accent bg-noc-accent/10"
                : "border-noc-border text-noc-muted hover:bg-noc-bg/40")
            }
          >
            {t(f.label)}
          </button>
        ))}
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {/* Tabela */}
      <div className="overflow-x-auto card-pad">
        <table className="w-full text-xs">
          <thead className="text-noc-muted border-b border-noc-border">
            <tr>
              <th className="text-left font-medium py-1.5 px-2">QR</th>
              <th className="text-left font-medium py-1.5 px-2">Dispositivo</th>
              <th className="text-left font-medium py-1.5 px-2">FW</th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colStatus")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colCustomer")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colInstaller")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">HB</th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colTemplate")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("inventory.colActions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="text-sm text-noc-muted/70
                                           italic py-3 px-2">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr>
                <td colSpan={9} className="text-sm text-noc-muted/60
                                           italic py-3 px-2">
                  {t("inventory.empty")}
                </td>
              </tr>
            )}
            {data.items.map((it) => (
              <tr key={it.id}
                  className="border-b border-noc-border/40
                             hover:bg-noc-bg/30">
                <td className="py-1.5 px-2 font-mono text-noc-accent">
                  {it.qr_serial}
                </td>
                <td className="py-1.5 px-2 font-mono text-noc-muted">
                  {it.device_id || "—"}
                </td>
                <td className="py-1.5 px-2">{it.fw || "—"}</td>
                <td className="py-1.5 px-2">
                  <StatusPill status={it.status} />
                  {it.offline && (
                    <span className="ml-1 text-[10px] text-noc-err">
                      ● {t("inventory.offlineTag")}
                    </span>
                  )}
                </td>
                <td className="py-1.5 px-2">
                  {it.customer_id ? `#${it.customer_id}` : "—"}
                </td>
                <td className="py-1.5 px-2">{it.installer || "—"}</td>
                <td className="py-1.5 px-2 text-noc-muted">
                  {it.last_hb_at ? formatRelative(it.last_hb_at) : "—"}
                </td>
                <td className="py-1.5 px-2 text-noc-muted/80">
                  {it.template_id ? "✓" : "—"}
                </td>
                <td className="py-1.5 px-2">
                  <div className="flex gap-1">
                    <Can
                      capability={CAP.INVENTORY_WRITE}
                      fallback={<span className="text-[10px] text-noc-muted/60">—</span>}
                    >
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-border hover:bg-noc-bg/50"
                      onClick={() => { setEditItem(it); setModal("edit"); }}
                    >
                      {t("inventory.btnEdit")}
                    </button>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-orange/30 text-noc-orange
                                 hover:bg-noc-orange/10"
                      onClick={() => quickStatus(it, "maintenance")}
                    >
                      {t("inventory.btnMaintenance")}
                    </button>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-err/30 text-noc-err
                                 hover:bg-noc-err/10"
                      onClick={() => quickStatus(it, "lost")}
                    >
                      {t("inventory.btnLost")}
                    </button>
                    </Can>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-noc-muted/70">
        {t("inventory.total")}: {data.total}
      </div>

      {/* Modais */}
      {modal === "new" && (
        <InventoryFormModal
          title={t("inventory.btnNew")}
          fields={["qr_serial", "device_id", "mac", "fw", "notes"]}
          onClose={() => setModal(null)}
          onSubmit={async (body) => {
            await createInventoryItem(body);
            setModal(null);
            reload();
          }}
        />
      )}
      {modal === "reserve" && (
        <InventoryFormModal
          title={t("inventory.btnReserve")}
          fields={[
            "qr_serial", "customer_id", "contract_id",
            "installer", "team", "scheduled_at", "notes",
          ]}
          onClose={() => setModal(null)}
          onSubmit={async (body) => {
            await reserveInventoryItem(body);
            setModal(null);
            reload();
          }}
        />
      )}
      {modal === "edit" && editItem && (
        <InventoryFormModal
          title={t("inventory.btnEdit")}
          fields={[
            "device_id", "mac", "fw", "status",
            "installer", "team", "location", "notes",
          ]}
          initial={editItem}
          onClose={() => { setModal(null); setEditItem(null); }}
          onSubmit={async (body) => {
            await updateInventoryItem(editItem.id, body);
            setModal(null);
            setEditItem(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function DashCard({ label, value, tone }) {
  const toneCls = {
    sky: "border-noc-accent/30 text-noc-accent",
    amber: "border-noc-warn/30 text-noc-warn",
    emerald: "border-noc-ok/30 text-noc-ok",
    orange: "border-noc-orange/30 text-noc-orange",
    red: "border-noc-err/30 text-noc-err",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`rounded border ${toneCls} bg-noc-bg/30 p-2`}>
      <div className="font-mono text-lg font-bold">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint">{label}</div>
    </div>
  );
}

/**
 * Modal de formulário genérico — usado para Novo / Reservar / Editar.
 * Renderiza um input por campo em `fields`. Não usa <form> (regra de
 * artifacts/NOC); o submit é por botão.
 */
function InventoryFormModal({ title, fields, initial, onClose, onSubmit }) {
  const [values, setValues] = useState(() => {
    const v = {};
    for (const f of fields) v[f] = (initial && initial[f]) || "";
    return v;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // PROVISIONING-PHYSICAL-QR-CAMERA-1 — scanner de câmera p/ o QR físico.
  const [scanOpen, setScanOpen] = useState(false);
  const hasQr = fields.includes("qr_serial");

  function setField(k, val) {
    setValues((prev) => ({ ...prev, [k]: val }));
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      // Só envia campos preenchidos.
      const body = {};
      for (const [k, v] of Object.entries(values)) {
        if (v === "" || v === null || v === undefined) continue;
        // QR físico: normaliza (remove espaços) antes de enviar.
        body[k] = k === "qr_serial" ? normalizePhysicalQr(v) : v;
      }
      await onSubmit(body);
    } catch (e) {
      setError(e?.message || String(e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-md space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">{title}</h3>
        <div className="space-y-2">
          {fields.map((f) =>
            f === "qr_serial" ? (
              <div key={f}>
                <label className="text-[11px] text-noc-muted block mb-0.5">
                  QR físico do Sonoff
                </label>
                <div className="flex gap-1">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={values[f]}
                    onChange={(e) => setField(f, e.target.value)}
                    placeholder="25082200178100"
                    className="flex-1 text-xs bg-noc-bg/60 border border-noc-border
                               rounded px-2 py-1 text-noc-text font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setScanOpen(true)}
                    title="Escanear com câmera"
                    className="text-xs px-2 py-1 rounded border border-noc-border
                               text-noc-muted hover:text-noc-text"
                  >
                    📷 Escanear
                  </button>
                </div>
                {values[f] && !isValidPhysicalQr(values[f]) && (
                  <p className="text-[10px] text-noc-warn mt-0.5">
                    {PHYSICAL_QR_ERROR}
                  </p>
                )}
              </div>
            ) : (
              <div key={f}>
                <label className="text-[11px] text-noc-muted block mb-0.5">
                  {t("inventory.field_" + f) || f}
                </label>
                <input
                  type={f === "scheduled_at" ? "datetime-local" : "text"}
                  value={values[f]}
                  onChange={(e) => setField(f, e.target.value)}
                  className="w-full text-xs bg-noc-bg/60 border
                             border-noc-border rounded px-2 py-1
                             text-noc-text"
                />
              </div>
            )
          )}
        </div>
        {hasQr && scanOpen && (
          <QrScannerModal
            onDetected={(value) => {
              setField("qr_serial", normalizePhysicalQr(value));
              setScanOpen(false);
            }}
            onManual={() => setScanOpen(false)}
            onClose={() => setScanOpen(false)}
          />
        )}
        {error && (
          <div className="text-[11px] text-noc-err bg-noc-err/10
                          border border-noc-err/30 rounded p-1.5">
            {error}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted
                       hover:bg-noc-bg/40"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-accent/30 text-noc-accent
                       bg-noc-accent/10 hover:bg-noc-accent/10
                       disabled:opacity-50"
            disabled={busy}
            onClick={submit}
          >
            {busy ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
